import { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Paper, Breadcrumbs, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Tabs, Tab, Grid } from '@mui/material';
import { useNotification } from '../../../components/Notification';
import { Add as AddIcon, Delete as DeleteIcon, MeetingRoom as DoorOpenIcon, NavigateNext as NavigateNextIcon, List as ListIcon, Category as CategoryIcon } from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import AddEntryExitReasonModal from './AddEntryExitReasonModal';
import EntryExitStatuses from '../EntryExitStatuses';
import AddEntryExitStatusModal from '../EntryExitStatuses/AddEntryExitStatusModal';
import { hasPermission } from '../../../utils/permissions';
import dayjs from 'dayjs';
import {
  fetchEntryExitStatuses,
  bulkDeleteEntryExitStatuses,
  addEntryExitStatus,
  selectEntryExitStatusesSuccessMessage as selectEntryExitStatusesSuccessMessageFromSlice,
  clearSuccessMessage as clearEntryExitStatusesSuccessMessage,
} from '../EntryExitStatuses/slice';
import {
    fetchEntryExitReasons,
    fetchEntryExitReasonById,
    addEntryExitReason,
    updateEntryExitReason,
    selectEntryExitReasons,
    selectEntryExitReasonsLoading,
    selectEntryExitReasonsError,
    selectEntryExitReasonsSuccessMessage,
    selectEntryExitReasonsTotal,
    selectEntryExitReasonsPage,
    selectEntryExitReasonsPageSize,
    selectEntryExitReasonsSortColumn,
    selectEntryExitReasonsSortDirection,
    selectEntryExitReasonsFilterName,
    selectEntryExitReasonsFilterStatusId,
    selectSelectedEntryExitReason,
    selectEntryExitStatuses,
    selectActiveTab,
    fetchInit,
    setPage,
    setPageSize,
    setSortColumn,
    setSortDirection,
    setFilterName,
    setFilterStatusId,
    setActiveTab,
    deleteEntryExitReason,
    bulkDeleteEntryExitReasons,
    clearError,
    clearSuccessMessage,
} from './slice';
import './EntryExitReasons.css';

// TabPanel component - defined outside to prevent re-creation on each render
function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`entry-exit-reasons-tabpanel-${index}`}
            aria-labelledby={`entry-exit-reasons-tab-${index}`}
            style={{ flex: 1, display: value === index ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>{children}</Box>
            )}
        </div>
    );
}

function EntryExitReasons() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const entryExitReasons = useSelector(selectEntryExitReasons);
    const loading = useSelector(selectEntryExitReasonsLoading);
    const error = useSelector(selectEntryExitReasonsError);
    const successMessage = useSelector(selectEntryExitReasonsSuccessMessage);
    const total = useSelector(selectEntryExitReasonsTotal);
    const page = useSelector(selectEntryExitReasonsPage);
    const pageSize = useSelector(selectEntryExitReasonsPageSize);
    const sortColumn = useSelector(selectEntryExitReasonsSortColumn);
    const sortDirection = useSelector(selectEntryExitReasonsSortDirection);
    const filterName = useSelector(selectEntryExitReasonsFilterName);
    const filterStatusId = useSelector(selectEntryExitReasonsFilterStatusId);
    const selectedEntryExitReason = useSelector(selectSelectedEntryExitReason);
    const entryExitStatuses = useSelector(selectEntryExitStatuses);
    const activeTab = useSelector(selectActiveTab);
    const entryExitStatusesSuccessMessage = useSelector(selectEntryExitStatusesSuccessMessageFromSlice);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [entryExitReasonToDelete, setEntryExitReasonToDelete] = useState(null);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);

    // Check permissions - assuming 'entryexitreason' module with standard CRUD permissions
    const canAddEntryExitReason = hasPermission('entryexitreason', 'addEntryExitReason');
    const canEditEntryExitReason = hasPermission('entryexitreason', 'editEntryExitReason');
    const canDeleteEntryExitReason = hasPermission('entryexitreason', 'deleteEntryExitReason');
    
    // EntryExitStatuses permissions
    const canAddEntryExitStatus = hasPermission('entryexitstatus', 'addEntryExitStatus');
    const canDeleteEntryExitStatus = hasPermission('entryexitstatus', 'deleteEntryExitStatus');
    
    // State for EntryExitStatuses buttons
    const [entryExitStatusesSelectedIds, setEntryExitStatusesSelectedIds] = useState([]);
    const [showEntryExitStatusAddDialog, setShowEntryExitStatusAddDialog] = useState(false);
    const [showEntryExitStatusBulkDeleteDialog, setShowEntryExitStatusBulkDeleteDialog] = useState(false);
    
    // Handlers for EntryExitStatuses - use useCallback to prevent re-creation
    const handleAddEntryExitStatus = useCallback(() => {
      setShowEntryExitStatusAddDialog(true);
    }, []);
    
    const handleCloseAddEntryExitStatus = useCallback(() => {
      setShowEntryExitStatusAddDialog(false);
    }, []);
    
    const handleConfirmAddEntryExitStatus = useCallback((entryExitStatusData) => {
      dispatch(addEntryExitStatus(entryExitStatusData));
      setShowEntryExitStatusAddDialog(false);
      // Refresh entry exit statuses list for modal dropdown
      setTimeout(() => {
        dispatch(fetchInit());
      }, 500); // Wait a bit for the add to complete
    }, [dispatch]);
    
    const handleBulkDeleteEntryExitStatuses = useCallback(() => {
      setShowEntryExitStatusBulkDeleteDialog(true);
    }, []);
    
    // Memoize the setter to prevent re-creation
    const handleEntryExitStatusesSelectedIdsChange = useCallback((ids) => {
      setEntryExitStatusesSelectedIds(ids);
    }, []);

    // Fetch init data on mount
    useEffect(() => {
        dispatch(fetchInit());
    }, [dispatch]);

    // Fetch entry/exit reasons whenever tab changes or when filters change
    useEffect(() => {
        dispatch(fetchEntryExitReasons({ page, pageSize, sortColumn, sortDirection, filterName, filterStatusId }));
    }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName, filterStatusId, activeTab]);

    // Show error notification
    useEffect(() => {
        if (error) {
            showNotification(error, 'error');
            dispatch(clearError());
        }
    }, [error, dispatch, showNotification]);

    // Show success notification
    useEffect(() => {
        if (successMessage) {
            showNotification(successMessage, 'success');
            dispatch(clearSuccessMessage());
            // Refetch entry/exit reasons to update the list
            dispatch(fetchEntryExitReasons({ page, pageSize, sortColumn, sortDirection, filterName, filterStatusId }));
            // Also refresh entry exit statuses list if we're on tab 1 (to update dropdown in modal)
            if (activeTab === 0) {
                dispatch(fetchInit());
            }
        }
    }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName, filterStatusId, activeTab]);

    // Listen to EntryExitStatuses success messages to refresh status list for modal dropdown
    useEffect(() => {
        if (entryExitStatusesSuccessMessage) {
            // Refresh entry exit statuses list for modal dropdown
            dispatch(fetchInit());
            dispatch(clearEntryExitStatusesSuccessMessage());
        }
    }, [entryExitStatusesSuccessMessage, dispatch]);

    // Helper function to escape HTML and prevent XSS
    const escapeHtml = (text) => {
        if (!text) return '-';
        const map = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;',
        };
        return text.replace(/[&<>"']/g, (m) => map[m]);
    };

    // Handle tab change - always fetch data for both tabs
    const handleTabChange = (event, newValue) => {
        dispatch(setActiveTab(newValue));
        // Reset selectedIds when switching tabs
        setSelectedIds([]);
        setEntryExitStatusesSelectedIds([]);
        // Fetch data for both tabs
        dispatch(fetchEntryExitReasons({ page, pageSize, sortColumn, sortDirection, filterName, filterStatusId }));
        dispatch(fetchEntryExitStatuses({ page: 1, pageSize: 10, sortColumn: 'name', sortDirection: 'asc', name: '' }));
        dispatch(fetchInit());
    };

    // Define table columns
    const columns = [
        {
            key: 'name',
            label: t('entryExitReasons.table.name'),
        },
        {
            key: 'description',
            label: t('entryExitReasons.table.description'),
            render: (value) => escapeHtml(value),
        },
        {
            key: 'statusName',
            label: t('entryExitReasons.table.status'),
            render: (value) => value || '-',
        },
        {
            key: 'createdOn',
            label: t('entryExitReasons.table.createdOn'),
            render: (value) => {
                if (!value) return '-';
                const date = dayjs(value);
                // Check if year is invalid (< 1950 indicates default/invalid DateTime value)
                if (date.year() < 1950) return '-';
                return date.format('DD/MM/YYYY');
            },
        },
    ];

    // Handle edit action
    const handleEdit = (row) => {
        // Always refresh entry exit statuses list to get latest data from tab 2
        dispatch(fetchInit());
        // Fetch entry/exit reason details by ID
        dispatch(fetchEntryExitReasonById(row.id));
        setShowEditDialog(true);
    };

    // Handle delete action - show confirmation dialog
    const handleDelete = (row) => {
        setEntryExitReasonToDelete(row);
        setShowDeleteDialog(true);
    };

    // Confirm delete action
    const handleConfirmDelete = () => {
        if (entryExitReasonToDelete?.id) {
            dispatch(deleteEntryExitReason(entryExitReasonToDelete.id));
        }
        setShowDeleteDialog(false);
        setEntryExitReasonToDelete(null);
    };

    // Cancel delete action
    const handleCancelDelete = () => {
        setShowDeleteDialog(false);
        setEntryExitReasonToDelete(null);
    };

    // Handle bulk delete - show confirmation dialog
    const handleBulkDelete = () => {
        setShowBulkDeleteDialog(true);
    };

    // Confirm bulk delete action
    const handleConfirmBulkDelete = () => {
        dispatch(bulkDeleteEntryExitReasons(selectedIds));
        setShowBulkDeleteDialog(false);
        setSelectedIds([]);
    };

    // Cancel bulk delete action
    const handleCancelBulkDelete = () => {
        setShowBulkDeleteDialog(false);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize) => {
        dispatch(setPageSize(newPageSize));
    };

    // Handle sort change
    const handleSortChange = (column, direction) => {
        dispatch(setSortColumn(column));
        dispatch(setSortDirection(direction));
        // fetchEntryExitReasons will be called automatically by useEffect
    };

    // Handle filter name change
    const handleFilterNameChange = (event) => {
        dispatch(setFilterName(event.target.value));
    };

    // Handle filter status change
    const handleFilterStatusIdChange = (event) => {
        const value = event.target.value === '' ? null : parseInt(event.target.value, 10);
        dispatch(setFilterStatusId(value));
    };

    // Handle add entry/exit reason - show dialog
    const handleAddEntryExitReason = () => {
        // Always refresh entry exit statuses list to get latest data from tab 2
        dispatch(fetchInit());
        setShowAddDialog(true);
    };

    // Handle close add entry/exit reason dialog
    const handleCloseAddEntryExitReason = () => {
        setShowAddDialog(false);
    };

    // Confirm add entry/exit reason
    const handleConfirmAddEntryExitReason = (entryExitReasonData) => {
        dispatch(addEntryExitReason(entryExitReasonData));
        setShowAddDialog(false);
    };

    // Handle close edit entry/exit reason dialog
    const handleCloseEditEntryExitReason = () => {
        setShowEditDialog(false);
    };

    // Confirm edit entry/exit reason
    const handleConfirmEditEntryExitReason = (entryExitReasonData) => {
        dispatch(updateEntryExitReason(entryExitReasonData));
        setShowEditDialog(false);
    };

    // Status options for filter
    const statusOptions = [
        { id: '', name: t('entryExitReasons.allStatuses') },
        ...entryExitStatuses.map((status) => ({
            id: status.id.toString(),
            name: status.name,
        })),
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DoorOpenIcon fontSize="small" />
                        <Typography variant="body2">{t('entryExitReasons.breadcrumb.catalog')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('entryExitReasons.breadcrumb.entryExitReasons')}
                    </Typography>
                </Breadcrumbs>

                {/* Page Title and Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                        {t('entryExitReasons.title')}
                    </Typography>

                    {/* Action Buttons - Show based on active tab */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {activeTab === 0 && (
                            <>
                                {canAddEntryExitReason && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddEntryExitReason}
                                        id="addEntryExitReason"
                                        name="addEntryExitReason"
                                        data-testid="entryExitReasons-button-addEntryExitReason"
                                    >
                                        {t('entryExitReasons.addEntryExitReason')}
                                    </Button>
                                )}
                                {canDeleteEntryExitReason && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleBulkDelete}
                                        disabled={selectedIds.length === 0}
                                        id="deleteSelected"
                                        name="deleteSelected"
                                        data-testid="entryExitReasons-button-deleteSelected"
                                    >
                                        {t('entryExitReasons.deleteSelected')}
                                    </Button>
                                )}
                            </>
                        )}
                        {activeTab === 1 && (
                            <>
                                {canAddEntryExitStatus && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddEntryExitStatus}
                                        id="addEntryExitStatus"
                                        name="addEntryExitStatus"
                                        data-testid="entryExitReasons-button-addEntryExitStatus"
                                    >
                                        {t('entryExitStatuses.addEntryExitStatus')}
                                    </Button>
                                )}
                                {canDeleteEntryExitStatus && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleBulkDeleteEntryExitStatuses}
                                        disabled={entryExitStatusesSelectedIds.length === 0}
                                        id="deleteSelectedEntryExitStatuses"
                                        name="deleteSelectedEntryExitStatuses"
                                        data-testid="entryExitReasons-button-deleteSelectedEntryExitStatuses"
                                    >
                                        {t('entryExitStatuses.deleteSelected')}
                                    </Button>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}
            >
                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="entry exit reasons tabs">
                        <Tab
                            icon={<ListIcon />}
                            iconPosition="start"
                            label={t('entryExitReasons.tabs.entryExitReasons')}
                            id="entry-exit-reasons-tab-0"
                            aria-controls="entry-exit-reasons-tabpanel-0"
                        />
                        <Tab
                            icon={<CategoryIcon />}
                            iconPosition="start"
                            label={t('entryExitReasons.tabs.entryExitStatuses')}
                            id="entry-exit-reasons-tab-1"
                            aria-controls="entry-exit-reasons-tabpanel-1"
                        />
                    </Tabs>
                </Box>

                {/* Entry Exit Reasons Tab */}
                <TabPanel value={activeTab} index={0}>
                    {/* Filter Section */}
                    <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{ width: 250 }}>
                                <Input
                                    label={t('entryExitReasons.filterByName')}
                                    value={filterName}
                                    onChange={handleFilterNameChange}
                                    placeholder={t('entryExitReasons.filterByName')}
                                    id="filterName"
                                    name="filterName"
                                    data-testid="entryExitReasons-input-filterName"
                                />
                            </Box>
                            <Box sx={{ width: 250 }}>
                                <Select
                                    label={t('entryExitReasons.filterByStatus')}
                                    value={filterStatusId?.toString() || ''}
                                    onChange={handleFilterStatusIdChange}
                                    options={statusOptions}
                                    id="filterStatusId"
                                    name="filterStatusId"
                                    data-testid="entryExitReasons-select-filterStatusId"
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* Table */}
                    <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff', minHeight: 0 }}>
                        <Table
                            id="entryExitReasons-table"
                            columns={columns}
                            data={entryExitReasons}
                            selectable={true}
                            selectedIds={selectedIds}
                            onSelectionChange={setSelectedIds}
                            onEdit={canEditEntryExitReason ? handleEdit : null}
                            onDelete={canDeleteEntryExitReason ? handleDelete : null}
                            loading={loading}
                            emptyMessage={t('entryExitReasons.noData')}
                            idField="id"
                            pagination={true}
                            page={page}
                            pageSize={pageSize}
                            totalItems={total}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            pageSizeOptions={[5, 10, 20, 50, 100]}
                            sortable={true}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            onSortChange={handleSortChange}
                            sortableColumns={['name', 'description', 'statusName']}
                        />
                    </Box>
                </TabPanel>

                {/* Entry Exit Statuses Tab */}
                <TabPanel value={activeTab} index={1}>
                    <EntryExitStatuses 
                        onSelectedIdsChange={handleEntryExitStatusesSelectedIdsChange}
                        onAddClick={handleAddEntryExitStatus}
                        onBulkDeleteClick={handleBulkDeleteEntryExitStatuses}
                        hideButtons={true}
                        isActive={activeTab === 1}
                    />
                </TabPanel>
            </Paper>

            {/* Confirm Delete Dialog */}
            <Dialog open={showDeleteDialog} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('entryExitReasons.deleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('entryExitReasons.deleteDialog.message')}</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelDelete}
                        variant="outlined"
                        id="cancelDelete"
                        name="cancelDelete"
                        data-testid="entryExitReasons-button-cancelDelete"
                    >
                        {t('entryExitReasons.deleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmDelete"
                        name="confirmDelete"
                        data-testid="entryExitReasons-button-confirmDelete"
                    >
                        {t('entryExitReasons.deleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Bulk Delete Dialog */}
            <Dialog open={showBulkDeleteDialog} onClose={handleCancelBulkDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('entryExitReasons.bulkDeleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('entryExitReasons.bulkDeleteDialog.message', {
                            count: selectedIds.length,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelBulkDelete}
                        variant="outlined"
                        id="cancelBulkDelete"
                        name="cancelBulkDelete"
                        data-testid="entryExitReasons-button-cancelBulkDelete"
                    >
                        {t('entryExitReasons.bulkDeleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmBulkDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmBulkDelete"
                        name="confirmBulkDelete"
                        data-testid="entryExitReasons-button-confirmBulkDelete"
                    >
                        {t('entryExitReasons.bulkDeleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add EntryExitReason Modal */}
            <AddEntryExitReasonModal 
                open={showAddDialog} 
                onClose={handleCloseAddEntryExitReason} 
                onConfirm={handleConfirmAddEntryExitReason}
                entryExitStatuses={entryExitStatuses}
            />

            {/* Edit EntryExitReason Modal */}
            <AddEntryExitReasonModal
                open={showEditDialog}
                onClose={handleCloseEditEntryExitReason}
                onConfirm={handleConfirmEditEntryExitReason}
                entryExitReasonData={selectedEntryExitReason}
                entryExitStatuses={entryExitStatuses}
                editMode={true}
            />

            {/* Add Entry Exit Status Modal */}
            <AddEntryExitStatusModal
                open={showEntryExitStatusAddDialog}
                onClose={handleCloseAddEntryExitStatus}
                onConfirm={handleConfirmAddEntryExitStatus}
            />

            {/* Confirm Bulk Delete Entry Exit Statuses Dialog */}
            <Dialog
                open={showEntryExitStatusBulkDeleteDialog}
                onClose={() => setShowEntryExitStatusBulkDeleteDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('entryExitStatuses.bulkDeleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('entryExitStatuses.bulkDeleteDialog.message', {
                            count: entryExitStatusesSelectedIds.length,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setShowEntryExitStatusBulkDeleteDialog(false)}
                        variant="outlined"
                        id="cancelBulkDeleteEntryExitStatuses"
                        name="cancelBulkDeleteEntryExitStatuses"
                        data-testid="entryExitReasons-button-cancelBulkDeleteEntryExitStatuses"
                    >
                        {t('entryExitStatuses.bulkDeleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={() => {
                            dispatch(bulkDeleteEntryExitStatuses(entryExitStatusesSelectedIds));
                            setShowEntryExitStatusBulkDeleteDialog(false);
                            setEntryExitStatusesSelectedIds([]);
                            // Refresh entry exit statuses list for modal dropdown
                            dispatch(fetchInit());
                        }}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmBulkDeleteEntryExitStatuses"
                        name="confirmBulkDeleteEntryExitStatuses"
                        data-testid="entryExitReasons-button-confirmBulkDeleteEntryExitStatuses"
                    >
                        {t('entryExitStatuses.bulkDeleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default EntryExitReasons;
